// src/pages/FulfillmentPage/components/InfoCard.tsx

import React from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Divider,
  Avatar,
  alpha,
  useTheme,
  Paper,
  Chip
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Fulfillment } from "../types";

interface InfoCardProps {
  fulfillment: Fulfillment;
}

export const InfoCard: React.FC<InfoCardProps> = ({ fulfillment }) => {
  const theme = useTheme();

  // Format dates
  const orderDate = new Date(fulfillment.order_created_at);
  const fulfillmentDate = new Date(fulfillment.created_at);
  
  // Calculate time difference
  const timeDiff = Math.abs(fulfillmentDate.getTime() - orderDate.getTime());
  const diffDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const timeToFulfill = diffDays === 0 ? 'Same day' : `${diffDays} day${diffDays > 1 ? 's' : ''} later`;

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3, 
        width: "100%", 
        overflow: "hidden",
        border: '1px solid',
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.2),
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.05)}`
        }
      }}
    >
      {/* Accent bar */}
      <Box 
        sx={{ 
          height: 4, 
          width: '100%', 
          bgcolor: theme.palette.primary.main,
          opacity: 0.8
        }} 
      />

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            <ReceiptIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Order Summary
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Timeline and total value
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={2.5}>
          {/* Timeline Section */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderColor: theme.palette.divider,
            }}
          >
            <Stack spacing={2}>
              {/* Order Created */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                  }}
                >
                  <CalendarTodayIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Order Created
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    {orderDate.toLocaleDateString(undefined, { 
                      weekday: 'short',
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                    {orderDate.toLocaleTimeString(undefined, { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Box>
              </Box>

              {/* Fulfillment Created */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Fulfillment Created
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    {fulfillmentDate.toLocaleDateString(undefined, { 
                      weekday: 'short',
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                    {fulfillmentDate.toLocaleTimeString(undefined, { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Box>
              </Box>

              {/* Time to Fulfill Chip */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Chip
                  label={timeToFulfill}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    color: theme.palette.text.secondary,
                    borderRadius: 1.5,
                    height: 24,
                  }}
                />
              </Box>
            </Stack>
          </Paper>

          {/* Order Total Section */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              borderColor: alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                  }}
                >
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Order Total
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Including all items and fees
                  </Typography>
                </Box>
              </Stack>
              
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 800, 
                  color: theme.palette.success.main,
                  lineHeight: 1.2
                }}>
                  ${Number(fulfillment.total || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                  CAD
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Quick Stats (optional - can remove if redundant) */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 1,
            mt: 1
          }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.3),
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                Order ID
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                #{fulfillment.order_number}
              </Typography>
            </Paper>
            
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.3),
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                Fulfillment ID
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                #{fulfillment.id}
              </Typography>
            </Paper>
          </Box>
        </Stack>
      </CardContent>
    </Paper>
  );
};